import React, { useState } from 'react';
import {
    useReactTable, flexRender,
    getCoreRowModel, getSortedRowModel, getFilteredRowModel,
} from '@tanstack/react-table';
import { DataTable, TableScroll, Input, EmptyState, Pill, CardHeaderRow } from '../../ui';
import { TriEntete, Muted } from '../../security/security.styled';

/* ═══════════════════════════════════════════════════════════════════
   Tableau des panneaux de la fiche utilisateur.

   Les trois listes liées (groupes, applications, appareils) utilisaient
   `TableSmall` : largeurs fixes en pixels, poignées de redimensionnement,
   en-têtes en anglais, booléens rendus en « true » / « false ». Elles suivent
   maintenant le même langage que les pages Sécurité — DataTable, Pill, Muted —
   pour qu'un même fait s'y lise de la même façon.

   Le tri et le filtre restent confiés à TanStack : c'est déjà la dépendance de
   tous les tableaux de l'application.
   ═══════════════════════════════════════════════════════════════════ */

// En dessous de ce nombre de lignes, un champ de filtre coûte plus d'attention
// qu'il n'en fait gagner : la liste tient à l'écran.
const SEUIL_RECHERCHE = 8;

/* Tri alphabétique français.
   La comparaison par défaut de TanStack compare des codes de caractères :
   « École » se retrouve après « Zurich », et « WKS-10 » avant « WKS-2 ».
   localeCompare range les accents à leur place et compare les nombres comme
   des nombres. À poser sur toute colonne de noms. */
export const triAlpha = (ligneA, ligneB, colonneId) => {
    const a = String(ligneA.getValue(colonneId) ?? '');
    const b = String(ligneB.getValue(colonneId) ?? '');
    return a.localeCompare(b, 'fr', { sensitivity: 'base', numeric: true });
};

function IconeTri({ etat }) {
    if (etat === 'asc') return <i className="bx bx-sort-up" />;
    if (etat === 'desc') return <i className="bx bx-sort-down" />;
    return <i className="bx bx-sort-alt-2" />;
}

export function PaneTable({
    titre, colonnes, donnees, placeholder = 'Filtrer…',
    badges = null, outils = null, vide, triInitial = [],
}) {
    const [filtre, setFiltre] = useState('');
    // Une liste liée arrive dans l'ordre où Mongo l'a rendue, c'est-à-dire dans
    // aucun ordre lisible. Les panneaux ouvrent donc triés sur le nom : c'est la
    // colonne par laquelle on cherche une ligne dans une liste de 87 groupes.
    const [tri, setTri] = useState(triInitial);

    const lignesSource = Array.isArray(donnees) ? donnees : [];

    const table = useReactTable({
        data: lignesSource,
        columns: colonnes,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: { sorting: tri, globalFilter: filtre },
        onSortingChange: setTri,
        onGlobalFilterChange: setFiltre,
    });

    const lignes = table.getRowModel().rows;
    const filtreActif = filtre.trim().length > 0;

    return (
        <>
            <CardHeaderRow style={{ flexWrap: 'wrap' }}>
                <h2>{titre}</h2>
                <Pill $tone="neutral">
                    {filtreActif
                        ? `${lignes.length} sur ${lignesSource.length}`
                        : lignesSource.length.toLocaleString('fr-FR')}
                </Pill>
                {badges}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {outils}
                    {lignesSource.length >= SEUIL_RECHERCHE && (
                        <Input
                            value={filtre}
                            onChange={(e) => setFiltre(e.target.value)}
                            placeholder={placeholder}
                            aria-label={`Filtrer ${titre.toLowerCase()}`}
                            style={{ width: '190px' }}
                        />
                    )}
                </div>
            </CardHeaderRow>

            {lignesSource.length === 0 ? (
                <EmptyState>
                    <i className={`bx ${vide?.icone || 'bx-inbox'}`} />
                    <h3>{vide?.titre || 'Aucune donnée'}</h3>
                    {vide?.texte && <p>{vide.texte}</p>}
                </EmptyState>
            ) : lignes.length === 0 ? (
                <EmptyState>
                    <i className="bx bx-search-alt" />
                    <h3>Aucun résultat</h3>
                    <p>Aucune ligne ne correspond à « {filtre} ».</p>
                </EmptyState>
            ) : (
                <TableScroll>
                    <DataTable>
                        <thead>
                            {table.getHeaderGroups().map((groupe) => (
                                <tr key={groupe.id}>
                                    {groupe.headers.map((entete) => (
                                        <th key={entete.id} style={entete.column.columnDef.meta?.style}>
                                            {entete.column.getCanSort() ? (
                                                <TriEntete
                                                    type="button"
                                                    onClick={entete.column.getToggleSortingHandler()}
                                                    data-trie={entete.column.getIsSorted() ? 'true' : 'false'}
                                                    title="Trier sur cette colonne"
                                                >
                                                    {flexRender(entete.column.columnDef.header, entete.getContext())}
                                                    <IconeTri etat={entete.column.getIsSorted()} />
                                                </TriEntete>
                                            ) : (
                                                flexRender(entete.column.columnDef.header, entete.getContext())
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {lignes.map((ligne) => (
                                <tr key={ligne.id}>
                                    {ligne.getVisibleCells().map((cellule) => (
                                        <td key={cellule.id}>
                                            {flexRender(cellule.column.columnDef.cell, cellule.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </DataTable>
                </TableScroll>
            )}
        </>
    );
}

// Un booléen à trois états — vrai, faux, non mesuré — et non deux. Sur le parc,
// 3 271 appareils n'ont pas de valeur pour `isManaged` : les afficher comme
// « non géré » inventerait un constat.
export function PillOuiNon({ valeur, oui, non, tonOui = 'ok', tonNon = 'danger' }) {
    if (valeur === null || valeur === undefined || valeur === '') return <Muted>—</Muted>;
    const vrai = valeur === true || valeur === 'true';
    return <Pill $tone={vrai ? tonOui : tonNon}>{vrai ? oui : non}</Pill>;
}
